import React from "react";
import { useNavigate } from "react-router-dom";
import { useScrollLock } from "../lib/scrollLock";
import { auth } from "../lib/auth";
import { ROUTES } from "../routes/paths";
import styles from "./AddActionModal.module.css";

interface AddActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShowToast?: (message: string) => void;
}

export function CreatePlaylistMenuIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z" />
    </svg>
  );
}

export function UploadLibraryMenuIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM10.5 15.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5c.24 0 .46.06.66.16V10h3.5v1.5h-2.16v4z" />
    </svg>
  );
}

export function ListenTogetherMenuIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}

export function VoiceTestMenuIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.09-.6-.39-1.14-1-1.14z" />
    </svg>
  );
}

export function AddActionModal({ isOpen, onClose, onShowToast }: AddActionModalProps) {
  const navigate = useNavigate();
  useScrollLock(isOpen);

  if (!isOpen) return null;

  const handleCreatePlaylist = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    const user = auth.user();
    if (!user || user.is_guest) {
      navigate(ROUTES.AUTH.LOGIN, { state: { from: ROUTES.PLAYLISTS_CREATE } });
    } else {
      navigate(ROUTES.PLAYLISTS_CREATE);
    }
  };

  const handleUploadLibrary = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    const user = auth.user();
    if (!user || user.is_guest) {
      navigate(ROUTES.AUTH.LOGIN, { state: { from: ROUTES.UPLOAD_SONGS } });
    } else {
      navigate(ROUTES.UPLOAD_SONGS);
    }
  };

  const handleListenTogether = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    if (onShowToast) {
      onShowToast("Listen Together feature is coming soon!");
    }
  };

  const handleVoiceTest = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
    const user = auth.user();
    if (!user || user.is_guest) {
      navigate(ROUTES.AUTH.LOGIN, { state: { from: ROUTES.VOICE_TEST } });
    } else {
      navigate(ROUTES.VOICE_TEST);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.optionsList}>
          <button type="button" className={styles.option} onClick={handleCreatePlaylist}>
            <span className={styles.icon}>
              <CreatePlaylistMenuIcon size={24} />
            </span>
            <span>Create Playlist</span>
          </button>

          <button type="button" className={styles.option} onClick={handleUploadLibrary}>
            <span className={styles.icon}>
              <UploadLibraryMenuIcon size={24} />
            </span>
            <span>Upload Library</span>
          </button>

          <button type="button" className={styles.option} onClick={handleListenTogether}>
            <span className={styles.icon}>
              <ListenTogetherMenuIcon size={24} />
            </span>
            <span>Listen Together</span>
          </button>

          <button type="button" className={styles.option} onClick={handleVoiceTest}>
            <span className={styles.icon}>
              <VoiceTestMenuIcon size={24} />
            </span>
            <span>Yours Voice Test</span>
          </button>
        </div>

        <div className={styles.cancelContainer}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
